import { Serializable } from "@/core/api/decorators/serializable_decorator"
import { SubAreaModel } from "@/modules/prices/models/subAreaModel"
import { TaskKanbanStatus } from "@/modules/tasks/core/constants/taskKanbanStatus"
import { FileAttachment } from "@/modules/tasks/customer/models/fileAttachment"
import { UserModel } from "@/modules/users/models/userModel"
import { CandidateAIReasoning } from "./candidateAIReasoning"
import { CandidateAvailabilityState } from "../constants/CandidateAvailabilityState"
import { CandidateAllocationPeriod } from "./candidateAllocationPeriod"
import { EndAssignmentReason } from "@/modules/assignments/constants/endAssignmentReason"
import { NegotiationModel } from "@/modules/candidate/models/negotiationModel"
import { NegotiationStatus } from "@/modules/candidate/constants/negotiationStatus"
import { CurrenciesDSVType } from "@/modules/prices/constants/currenciesDSV"
import { HiringLocation } from "@/modules/tasks/models/rfs/hiringLocation"
import { ContractStatus } from "@/modules/assignments/constants/contractStatus"
import { CandidateSignalStatus } from "../constants/CandidateSignalStatus"
import { CandidateSignalAction } from "../constants/CandidateSignalAction"
import { ExtensionStatus } from "@/modules/extension/constants/extensionStatus"
import { EndConsultantRequestStatus } from "@/modules/end_consultant/constants/endConsultantRequestStatus"
import { ContractHistoryItemModel } from "./contractHistoryItemModel"
import { ServiceType } from "@/modules/tasks/active/constants/serviceType"
import { EngagementType } from "@/modules/tasks/active/constants/engagementType"
import { ConsultantStatus } from "@/modules/consultant/constants/consultantStatus"
import { UserManagerModel } from "@/modules/users/models/userManagerModel"
import { OnboardingRequirementModel } from "@/modules/tasks/models/rfs/onboardingRequirementModel"
import { CompanyListModel } from "@/modules/company/models/companyListModel"

@Serializable
export class CandidateModel {
  uid: string = ""
  version: number = 0
  allocation: number = 0
  availability: number = CandidateAvailabilityState.NotDefined
  availabilityInfo: string = ""
  startDate: string = ""
  endDate: string = ""
  kanbanStatus: number = TaskKanbanStatus.NotDefined
  numYearsExperience: number = 0
  periods: CandidateAllocationPeriod[] = []
  numHoursWeek: number = 37
  intermediateSale: boolean = false
  isInternal: boolean = false
  consultantId: string = ""
  companyId: string = ""
  companyName: string = ""
  priceValue: number = 0 // Talent selected price
  maxHoursInContract: number = 0 // AdHoc: per experience-level max hours for this candidate
  priceYour: number = 0.0 // Owner selected price
  priceValueCurrency: CurrenciesDSVType | "" = ""
  localPriceValue: number = 0.0
  localPriceValueCurrency: CurrenciesDSVType | "" = ""
  addedDate: string = ""
  nominatedDate: string = ""
  isDraft: boolean = false
  subArea: SubAreaModel | null = null
  entity: CompanyListModel | null = null
  user: UserModel | null = null
  responsible: UserModel | null = null
  leads: UserModel[] = []
  files: FileAttachment[] = []
  reasoning: CandidateAIReasoning | null = null
  currency: string = "" // Backward compatibility only, will not be set if version > 0
  endReason: number = EndAssignmentReason.NotDefined
  endedAsPlanned: boolean = false
  negotiationState: number = NegotiationStatus.NotDefined
  negotiation: NegotiationModel | null = null
  selectedLocation: HiringLocation | null = null
  contractId: string = ""
  hasContract: boolean = false
  contractEndDate: string = ""
  countryCode: string = ""
  countryName: string = ""
  companyVATNumber: string = ""
  companyAddress: string = ""
  extensionId: string = ""
  contractStatus: ContractStatus = ContractStatus.NotDefined
  signalStatus: CandidateSignalStatus = CandidateSignalStatus.Attention
  signalAction: CandidateSignalAction = CandidateSignalAction.NoActionRequired
  extensionStatus: ExtensionStatus = ExtensionStatus.NotDefined
  latestExtensionOfferStatus: number = 0
  endRequestId: string = ""
  endRequestStatus: EndConsultantRequestStatus =
    EndConsultantRequestStatus.NotDefined
  originalEndDate: string = ""
  isSigned: boolean = false
  hasPendingContractApproval: boolean = false
  hasSentPendingContractApproval: boolean = false

  hasActiveContract: boolean = false
  contractHistory: ContractHistoryItemModel[] = []
  serviceType: number = ServiceType.Consultant
  costCenterId: string = ""
  costCenterName: string = ""
  taskId: string = ""
  email: string = ""
  phone: string = ""
  workEmail: string = ""
  title: string = ""
  engagementType: EngagementType = EngagementType.TimeAndMaterial
  status: ConsultantStatus = ConsultantStatus.NotStarted
  requester: UserModel | null = null
  managers: UserManagerModel[] = []
  onboardingRequirements: OnboardingRequirementModel =
    new OnboardingRequirementModel()
  constructor(init?: Partial<CandidateModel>) {
    this.periods = (init?.periods ?? []).map(
      (period) => new CandidateAllocationPeriod(period),
    )
    this.requester = init?.requester || new UserModel()
    this.subArea = init?.subArea || new SubAreaModel()
    this.user = init?.user || new UserModel()
    this.leads = (init?.leads ?? []).map((user) => new UserModel(user))
    this.files = (init?.files ?? []).map((file) => new FileAttachment(file))
    this.reasoning = init?.reasoning || new CandidateAIReasoning()
    this.negotiation = init?.negotiation || new NegotiationModel()
    this.selectedLocation = init?.selectedLocation || new HiringLocation()
    this.contractHistory = (init?.contractHistory ?? []).map(
      (assignment) => new ContractHistoryItemModel(assignment),
    )
    this.managers = (init?.managers ?? []).map(
      (manager) => new UserManagerModel(manager),
    )
    this.onboardingRequirements =
      init?.onboardingRequirements || new OnboardingRequirementModel()
    Object.assign(this, init)
    this.consultantId = init?.consultantId ?? ""
    this.engagementType = init?.engagementType || EngagementType.TimeAndMaterial
    this.serviceType = init?.serviceType || ServiceType.Consultant
    this.responsible = init?.responsible || new UserModel()
    this.entity = init?.entity || new CompanyListModel()
    Object.assign(this, init)

    this.periods = (init?.periods ?? []).map(
      (period) => new CandidateAllocationPeriod(period),
    )
    this.requester = init?.requester ? new UserModel(init.requester) : null
    this.subArea = init?.subArea ? new SubAreaModel(init.subArea) : null
    this.entity = init?.entity ? new CompanyListModel(init.entity) : null
    this.user = init?.user ? new UserModel(init.user) : null
    this.responsible = init?.responsible
      ? new UserModel(init.responsible)
      : null
    this.leads = (init?.leads ?? []).map((lead) => new UserModel(lead))
    this.files = (init?.files ?? []).map((file) => new FileAttachment(file))
    this.reasoning = init?.reasoning
      ? new CandidateAIReasoning(init.reasoning)
      : null
    this.negotiation = init?.negotiation
      ? new NegotiationModel(init.negotiation)
      : null
    this.selectedLocation = init?.selectedLocation
      ? new HiringLocation(init.selectedLocation)
      : null
    this.contractHistory = (init?.contractHistory ?? []).map(
      (assignment) => new ContractHistoryItemModel(assignment),
    )
    this.managers = (init?.managers ?? []).map(
      (manager) => new UserManagerModel(manager),
    )
    this.onboardingRequirements = init?.onboardingRequirements
      ? new OnboardingRequirementModel(init.onboardingRequirements)
      : new OnboardingRequirementModel()
  }
}
